import React, {
  useState, useEffect, useMemo, useRef,
} from 'react';
import {
  Button,
  ActionRow,
  ModalDialog,
  Form,
  Alert,
  Badge,
  Spinner,
  Row,
  Col,
  Icon,
  Tooltip,
  OverlayTrigger,
} from '@openedx/paragon';
import { Edit, ContentCopy, Check } from '@openedx/paragon/icons';
import {
  validateEmail,
  validateZipCode,
  validateStateCode,
  isStateField,
  isZipCodeField,
  createLearnerProfiles,
  parseCSV,
  downloadCSV,
  generateCSVTemplate,
  validateCSVLearner,
} from '../../utils';
import { logStudioHomeError } from '../../../logging';
import { getCatalogRemainingAvailability } from './catalogUtils';

interface LearnerFormData {
  firstName: string;
  lastName: string;
  email: string;
  customFields: Record<string, string>;
  selectedLicenses: string[];
}

interface ValidationErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  customFields?: Record<string, string>;
}

interface LicenseOption {
  id: string;
  name: string;
  type: 'legacy_bundle' | 'catalog_course' | 'program';
  available: number | null;
  used: number;
  disabled: boolean;
  course_list?: string[]; // For legacy bundles - contains course IDs
  displayName?: string; // Resolved human-readable course name (catalog_course only)
  courseIds?: string[]; // Course IDs contained in this program (program type only), used for search
}

interface BulkErrorLearner extends LearnerFormData {
  errors: ValidationErrors;
  rowNumber: number;
}

interface SelectedCompany {
  company_name: string;
  friendly_name: string;
  custom_csv_fields?: string[];
}

interface CreateLearnersModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedCompany: SelectedCompany | null;
  licenseOptions: LicenseOption[];
  licenseAllocation: Record<string, number>;
  onLicenseAllocationChange: (allocation: Record<string, number>) => void;
}

const CreateLearnersModal: React.FC<CreateLearnersModalProps> = ({
  isOpen,
  onClose,
  selectedCompany,
  licenseOptions,
  licenseAllocation,
  onLicenseAllocationChange,
}) => {
  // Form state for current learner
  const [currentLearner, setCurrentLearner] = useState<LearnerFormData>({
    firstName: '',
    lastName: '',
    email: '',
    customFields: {},
    selectedLicenses: [],
  });

  // List of learners to be submitted
  const [learnersList, setLearnersList] = useState<LearnerFormData[]>([]);

  // Index of the learner currently being edited (null = add new mode)
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  // Validation errors
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<string>('');
  const [submissionSuccess, setSubmissionSuccess] = useState<string>('');
  const [submissionTaskId, setSubmissionTaskId] = useState<string | null>(null);
  const [taskIdCopied, setTaskIdCopied] = useState(false);

  // Refs for the visible read-only inputs — the actual clipboard sources
  const taskIdCopyInputRef = useRef<HTMLInputElement>(null);

  // Bulk upload state
  const [isBulkUploadMode, setIsBulkUploadMode] = useState(false);

  // License list search
  const [licenseSearchTerm, setLicenseSearchTerm] = useState('');
  const [bulkErrorLearners, setBulkErrorLearners] = useState<BulkErrorLearner[]>([]);
  const [uploadStatus, setUploadStatus] = useState<{ message: string; variant: 'success' | 'danger' | 'info' }>({
    message: '',
    variant: 'info',
  });

  // Initialize custom fields when company changes
  useEffect(() => {
    if (selectedCompany?.custom_csv_fields) {
      const initialCustomFields: Record<string, string> = {};
      selectedCompany.custom_csv_fields.forEach(field => {
        initialCustomFields[field] = '';
      });
      setCurrentLearner(prev => ({ ...prev, customFields: initialCustomFields }));
    }
  }, [selectedCompany?.custom_csv_fields]);

  // Validate current form
  const validateForm = (): boolean => {
    const errors: ValidationErrors = {};
    let isValid = true;

    // First name
    if (!currentLearner.firstName.trim()) {
      errors.firstName = 'First name is required';
      isValid = false;
    }

    // Last name
    if (!currentLearner.lastName.trim()) {
      errors.lastName = 'Last name is required';
      isValid = false;
    }

    // Email
    if (!currentLearner.email.trim()) {
      errors.email = 'Email is required';
      isValid = false;
    } else if (!validateEmail(currentLearner.email)) {
      errors.email = 'Invalid email format';
      isValid = false;
    } else {
      // Check for duplicate email in learners list
      const isDuplicate = learnersList.some(
        learner => learner.email.toLowerCase() === currentLearner.email.toLowerCase(),
      );
      if (isDuplicate) {
        errors.email = 'This email has already been added';
        isValid = false;
      }
    }

    // Custom fields with conditional validation for state/zip
    const customFieldErrors: Record<string, string> = {};
    if (selectedCompany?.custom_csv_fields) {
      selectedCompany.custom_csv_fields.forEach(field => {
        const value = currentLearner.customFields[field];

        // Check if this is a state field
        if (isStateField(field)) {
          if (!value?.trim()) {
            customFieldErrors[field] = `${field} is required`;
            isValid = false;
          } else if (!validateStateCode(value)) {
            customFieldErrors[field] = 'Invalid state code (use 2-letter abbreviation like CA, NY)';
            isValid = false;
          }
        } else if (isZipCodeField(field)) {
          if (!value?.trim()) {
            customFieldErrors[field] = `${field} is required`;
            isValid = false;
          } else if (!validateZipCode(value)) {
            customFieldErrors[field] = 'Invalid zip code format (e.g., 12345 or 12345-6789)';
            isValid = false;
          }
        } else if (!value?.trim()) {
          customFieldErrors[field] = `${field} is required`;
          isValid = false;
        }
      });
    }
    if (Object.keys(customFieldErrors).length > 0) {
      errors.customFields = customFieldErrors;
    }

    setValidationErrors(errors);
    return isValid;
  };

  // Handle input changes
  const handleInputChange = (field: keyof LearnerFormData, value: any) => {
    setCurrentLearner(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[field as keyof ValidationErrors];
      return newErrors;
    });
  };

  // Handle custom field changes
  const handleCustomFieldChange = (fieldName: string, value: string) => {
    setCurrentLearner(prev => ({
      ...prev,
      customFields: {
        ...prev.customFields,
        [fieldName]: value,
      },
    }));
    // Clear error for this custom field
    setValidationErrors(prev => {
      if (prev.customFields) {
        const newCustomFieldErrors = { ...prev.customFields };
        delete newCustomFieldErrors[fieldName];
        return {
          ...prev,
          customFields: Object.keys(newCustomFieldErrors).length > 0 ? newCustomFieldErrors : undefined,
        };
      }
      return prev;
    });
  };

  // Handle license selection changes
  const handleLicenseSelectionChange = (licenseId: string, isSelected: boolean) => {
    setCurrentLearner(prev => ({
      ...prev,
      selectedLicenses: isSelected
        ? [...prev.selectedLicenses, licenseId]
        : prev.selectedLicenses.filter(id => id !== licenseId),
    }));
  };

  // Build a blank learner form state
  const buildEmptyLearner = (): LearnerFormData => {
    const initialCustomFields: Record<string, string> = {};
    selectedCompany?.custom_csv_fields?.forEach(field => {
      initialCustomFields[field] = '';
    });
    return {
      firstName: '',
      lastName: '',
      email: '',
      customFields: initialCustomFields,
      selectedLicenses: [],
    };
  };

  // Add (or save edit of) learner to list
  const handleAddLearner = () => {
    if (!validateForm()) {
      return;
    }

    if (editingIndex !== null) {
      // Re-insert the edited learner at the original index
      setLearnersList(prev => {
        const updated = [...prev];
        updated.splice(editingIndex, 0, { ...currentLearner });
        return updated;
      });
      setEditingIndex(null);
    } else {
      // Append new learner
      setLearnersList(prev => [...prev, { ...currentLearner }]);
    }

    // Update license allocation
    const newAllocation = { ...licenseAllocation };
    currentLearner.selectedLicenses.forEach(licenseId => {
      newAllocation[licenseId] = (newAllocation[licenseId] || 0) + 1;
    });
    onLicenseAllocationChange(newAllocation);

    // Reset form
    setCurrentLearner(buildEmptyLearner());
    setValidationErrors({});
  };

  // Begin editing a staged learner
  const handleEditLearner = (index: number) => {
    const learnerToEdit = learnersList[index];

    // Reverse license allocation for this learner
    const newAllocation = { ...licenseAllocation };
    learnerToEdit.selectedLicenses.forEach(licenseId => {
      newAllocation[licenseId] = Math.max(0, (newAllocation[licenseId] || 0) - 1);
    });
    onLicenseAllocationChange(newAllocation);

    // Remove from list and populate form
    setLearnersList(prev => prev.filter((_, i) => i !== index));
    setCurrentLearner({ ...learnerToEdit });
    setEditingIndex(index);
    setValidationErrors({});
  };

  // Cancel editing — restore learner back to its original index
  const handleCancelEdit = () => {
    if (editingIndex === null) { return; }

    const learnerToRestore = { ...currentLearner };

    // Re-apply license allocation
    const newAllocation = { ...licenseAllocation };
    learnerToRestore.selectedLicenses.forEach(licenseId => {
      newAllocation[licenseId] = (newAllocation[licenseId] || 0) + 1;
    });
    onLicenseAllocationChange(newAllocation);

    // Re-insert at original index
    setLearnersList(prev => {
      const updated = [...prev];
      updated.splice(editingIndex, 0, learnerToRestore);
      return updated;
    });

    // Reset form and edit state
    setCurrentLearner(buildEmptyLearner());
    setValidationErrors({});
    setEditingIndex(null);
  };

  // Remove learner from list (✕ button — does NOT enter edit mode)
  const handleRemoveLearner = (index: number) => {
    const learnerToRemove = learnersList[index];

    // Update license allocation
    const newAllocation = { ...licenseAllocation };
    learnerToRemove.selectedLicenses.forEach(licenseId => {
      newAllocation[licenseId] = Math.max(0, (newAllocation[licenseId] || 0) - 1);
    });
    onLicenseAllocationChange(newAllocation);

    // Remove from list
    setLearnersList(prev => prev.filter((_, i) => i !== index));
  };

  // Submit all learners
  const handleSubmitAll = async () => {
    if (learnersList.length === 0) {
      setSubmissionError('Please add at least one learner before submitting');
      return;
    }

    if (!selectedCompany) {
      setSubmissionError('No company selected');
      return;
    }

    setIsSubmitting(true);
    setSubmissionError('');
    setSubmissionSuccess('');

    try {
      // Create profiles and enroll in courses/pathways
      // Prepare learners data with selected licenses for enrollments
      const learnersData = learnersList.map(learner => ({
        email: learner.email,
        firstName: learner.firstName,
        lastName: learner.lastName,
        customFields: learner.customFields,
        selectedLicenses: learner.selectedLicenses,
      }));

      // Create profiles and enroll in courses/pathways
      // The bulk registration endpoint handles both user creation and enrollments
      const profileResult = await createLearnerProfiles({
        companyName: selectedCompany.company_name,
        companyFriendlyName: selectedCompany.friendly_name,
        learners: learnersData,
        licenseOptions,
      });

      // Show success message
      let successMessage = `Successfully submitted ${learnersList.length} learner(s) for processing. `;
      if (profileResult.created && profileResult.created.length > 0) {
        successMessage += `${profileResult.created.length} profile(s) created. `;
      }
      if (profileResult.existing && profileResult.existing.length > 0) {
        successMessage += `${profileResult.existing.length} existing profile(s) updated. `;
      }
      if (profileResult.errors && profileResult.errors.length > 0) {
        successMessage += `${profileResult.errors.length} error(s) occurred.`;
      }

      setSubmissionSuccess(successMessage);
      setSubmissionTaskId(profileResult.taskId || null);

      // Clear learners list after successful submission
      setLearnersList([]);
      onLicenseAllocationChange({});
    } catch (error) {
      logStudioHomeError('cmp_learner_creation_submit_failed', error);
      setSubmissionError('Failed to create learners. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle template download
  const handleDownloadTemplate = () => {
    const csvContent = generateCSVTemplate(
      selectedCompany?.custom_csv_fields || [],
      licenseOptions,
    );
    const date = new Date().toISOString().split('T')[0];
    const filename = `learners_template_${selectedCompany?.company_name || 'company'}_${date}.csv`;
    downloadCSV(csvContent, filename);
  };

  // Handle CSV file upload
  const handleCSVUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) { return; }

    setUploadStatus({ message: '', variant: 'info' });

    try {
      const text = await file.text();
      const rows = parseCSV(text);

      if (rows.length < 2) {
        setUploadStatus({
          message: 'CSV file must contain at least a header row and one data row',
          variant: 'danger',
        });
        return;
      }

      const headers = rows[0];
      const dataRows = rows.slice(1);

      // Build set of existing emails for duplicate checking
      const existingEmails = new Set(
        learnersList.map(l => l.email.toLowerCase()),
      );

      const validLearners: LearnerFormData[] = [];
      const errorLearners: BulkErrorLearner[] = [];

      dataRows.forEach((row, index) => {
        const result = validateCSVLearner(
          row,
          headers,
          index + 2,
          existingEmails,
          selectedCompany?.custom_csv_fields || [],
          licenseOptions,
        );

        if (!result) { return; } // Skip malformed rows

        const { learner, errors } = result;

        // Check for duplicate - add to error form instead of skipping
        if (existingEmails.has(learner.email.toLowerCase())) {
          errorLearners.push({
            ...learner,
            errors: { ...errors, email: 'Duplicate email' },
            rowNumber: index + 2,
          });
          return;
        }

        if (Object.keys(errors).length === 0) {
          validLearners.push(learner);
          existingEmails.add(learner.email.toLowerCase());
        } else {
          errorLearners.push({
            ...learner,
            errors,
            rowNumber: index + 2,
          });
        }
      });

      // Add valid learners to main list
      if (validLearners.length > 0) {
        setLearnersList(prev => [...prev, ...validLearners]);

        // Update license allocation
        const newAllocation = { ...licenseAllocation };
        validLearners.forEach(learner => {
          learner.selectedLicenses.forEach(licenseId => {
            newAllocation[licenseId] = (newAllocation[licenseId] || 0) + 1;
          });
        });
        onLicenseAllocationChange(newAllocation);
      }

      // Set error learners for correction
      if (errorLearners.length > 0) {
        setBulkErrorLearners(prev => [...prev, ...errorLearners]);
      }

      // Show status message
      let message = '';
      if (validLearners.length > 0) {
        message += `${validLearners.length} learner(s) added successfully. `;
      }
      if (errorLearners.length > 0) {
        message += `${errorLearners.length} learner(s) have errors and need correction below.`;
      }

      setUploadStatus({
        message: message || 'No valid learners found in CSV',
        variant: validLearners.length > 0 ? 'success' : 'danger',
      });

      // Reset file input
      const fileInput = event.target as HTMLInputElement;
      fileInput.value = '';
    } catch (error) {
      logStudioHomeError('cmp_learner_csv_parse_failed', error);
      setUploadStatus({
        message: 'Error reading CSV file. Please check the file format.',
        variant: 'danger',
      });
    }
  };

  // Validate a single field for bulk error learner
  const validateBulkErrorField = (
    learner: BulkErrorLearner,
    field: keyof LearnerFormData,
    value: any,
    index: number,
  ): string | undefined => {
    switch (field) {
      case 'firstName':
        return !value.trim() ? 'First name is required' : undefined;
      case 'lastName':
        return !value.trim() ? 'Last name is required' : undefined;
      case 'email':
        if (!value.trim()) { return 'Email is required'; }
        if (!validateEmail(value)) { return 'Invalid email format'; }
        // Check for duplicates
        return (
          learnersList.some(l => l.email.toLowerCase() === value.toLowerCase())
          || bulkErrorLearners.some((l, i) => i !== index && l.email.toLowerCase() === value.toLowerCase())
        ) ? 'Duplicate email' : undefined;
      default:
        return undefined;
    }
  };

  // Handle bulk error learner field change with real-time validation
  const handleBulkErrorLearnerChange = (
    index: number,
    field: keyof LearnerFormData,
    value: any,
  ) => {
    setBulkErrorLearners(prev => {
      const updated = [...prev];
      const learner = updated[index];

      // Update the field value
      updated[index] = {
        ...learner,
        [field]: value,
      };

      // Validate the field in real-time
      const errorMessage = validateBulkErrorField(updated[index], field, value, index);

      // Update errors object
      const newErrors = { ...learner.errors };
      if (errorMessage) {
        (newErrors as any)[field] = errorMessage;
      } else {
        delete (newErrors as any)[field];
      }

      updated[index].errors = newErrors;

      return updated;
    });
  };

  // Handle bulk error custom field change with real-time validation
  const handleBulkErrorCustomFieldChange = (
    index: number,
    fieldName: string,
    value: string,
  ) => {
    setBulkErrorLearners(prev => {
      const updated = [...prev];
      const learner = updated[index];

      // Update the custom field value
      updated[index] = {
        ...learner,
        customFields: {
          ...learner.customFields,
          [fieldName]: value,
        },
      };

      // Validate the custom field in real-time
      const errorMessage = !value.trim() ? `${fieldName} is required` : undefined;

      // Update custom field errors
      const newErrors = { ...learner.errors };
      if (!newErrors.customFields) {
        newErrors.customFields = {};
      }
      const newCustomFieldErrors = { ...newErrors.customFields };

      if (errorMessage) {
        newCustomFieldErrors[fieldName] = errorMessage;
      } else {
        delete newCustomFieldErrors[fieldName];
      }

      // Clean up customFields object if empty
      if (Object.keys(newCustomFieldErrors).length === 0) {
        delete newErrors.customFields;
      } else {
        newErrors.customFields = newCustomFieldErrors;
      }

      updated[index].errors = newErrors;

      return updated;
    });
  };

  // Fix and add a single bulk error learner
  const handleFixBulkErrorLearner = (index: number) => {
    const learner = bulkErrorLearners[index];
    const errors: ValidationErrors = {};
    let isValid = true;

    // Validate
    if (!learner.firstName.trim()) {
      errors.firstName = 'First name is required';
      isValid = false;
    }
    if (!learner.lastName.trim()) {
      errors.lastName = 'Last name is required';
      isValid = false;
    }
    if (!learner.email.trim()) {
      errors.email = 'Email is required';
      isValid = false;
    } else if (!validateEmail(learner.email)) {
      errors.email = 'Invalid email format';
      isValid = false;
    } else if (
      learnersList.some(l => l.email.toLowerCase() === learner.email.toLowerCase())
      || bulkErrorLearners.some((l, i) => i !== index && l.email.toLowerCase() === learner.email.toLowerCase())
    ) {
      errors.email = 'Duplicate email';
      isValid = false;
    }

    // Custom fields with conditional validation for state/zip
    const customFieldErrors: Record<string, string> = {};
    if (selectedCompany?.custom_csv_fields) {
      selectedCompany.custom_csv_fields.forEach(field => {
        const value = learner.customFields[field];

        // Check if this is a state field
        if (isStateField(field)) {
          if (!value?.trim()) {
            customFieldErrors[field] = `${field} is required`;
            isValid = false;
          } else if (!validateStateCode(value)) {
            customFieldErrors[field] = 'Invalid state code (use 2-letter abbreviation like CA, NY)';
            isValid = false;
          }
        } else if (isZipCodeField(field)) {
          if (!value?.trim()) {
            customFieldErrors[field] = `${field} is required`;
            isValid = false;
          } else if (!validateZipCode(value)) {
            customFieldErrors[field] = 'Invalid zip code format (e.g., 12345 or 12345-6789)';
            isValid = false;
          }
        } else if (!value?.trim()) {
          customFieldErrors[field] = `${field} is required`;
          isValid = false;
        }
      });
    }
    if (Object.keys(customFieldErrors).length > 0) {
      errors.customFields = customFieldErrors;
    }

    if (!isValid) {
      // Update errors
      setBulkErrorLearners(prev => {
        const updated = [...prev];
        updated[index] = { ...updated[index], errors };
        return updated;
      });
      return;
    }

    // Add to main list
    const learnerData = { ...learner };
    delete learnerData.errors;
    delete learnerData.rowNumber;
    setLearnersList(prev => [...prev, learnerData]);

    // Update license allocation
    const newAllocation = { ...licenseAllocation };
    learner.selectedLicenses.forEach(licenseId => {
      newAllocation[licenseId] = (newAllocation[licenseId] || 0) + 1;
    });
    onLicenseAllocationChange(newAllocation);

    // Remove from error list
    setBulkErrorLearners(prev => {
      const updated = prev.filter((_, i) => i !== index);

      // If this was the last error, clear the upload status message
      if (updated.length === 0) {
        setUploadStatus({ message: '', variant: 'info' });
      }

      return updated;
    });
  };

  // Remove bulk error learner
  const handleRemoveBulkErrorLearner = (index: number) => {
    setBulkErrorLearners(prev => prev.filter((_, i) => i !== index));
  };

  // Check if form is valid and get validation message
  const formValidation = useMemo(() => {
    const issues: string[] = [];

    if (currentLearner.firstName.trim() === '') { issues.push('First name required'); }
    if (currentLearner.lastName.trim() === '') { issues.push('Last name required'); }
    if (currentLearner.email.trim() === '') {
      issues.push('Email required');
    } else {
      if (!validateEmail(currentLearner.email)) { issues.push('Invalid email format'); }
      if (learnersList.some(l => l.email.toLowerCase() === currentLearner.email.toLowerCase())) {
        issues.push('Email must be unique');
      }
    }

    // Check custom fields with conditional validation for state/zip
    if (selectedCompany?.custom_csv_fields) {
      selectedCompany.custom_csv_fields.forEach(field => {
        const value = currentLearner.customFields[field];

        if (isStateField(field)) {
          if (!value?.trim()) {
            issues.push(`${field} is required`);
          } else if (!validateStateCode(value)) {
            issues.push(`Invalid state code for ${field}`);
          }
        } else if (isZipCodeField(field)) {
          if (!value?.trim()) {
            issues.push(`${field} is required`);
          } else if (!validateZipCode(value)) {
            issues.push(`Invalid zip code format for ${field}`);
          }
        } else if (!value?.trim()) {
          issues.push(`${field} is required`);
        }
      });
    }

    return {
      isValid: issues.length === 0,
      message: issues.length > 0 ? issues[0] : '',
    };
  }, [currentLearner, learnersList, selectedCompany]);

  // Copy from a visible <input readonly> — reliable in Chrome Incognito, HTTP, and modals.
  // The browser always permits execCommand('copy') on a rendered, selected input element.
  const copyFromInput = (
    inputRef: React.RefObject<HTMLInputElement>,
    onSuccess: () => void,
  ) => {
    if (!inputRef.current) { return; }
    inputRef.current.focus();
    inputRef.current.select();
    inputRef.current.setSelectionRange(0, inputRef.current.value.length);
    document.execCommand('copy');
    onSuccess();
  };

  const handleCopyTaskId = () => {
    if (!submissionTaskId) { return; }
    copyFromInput(taskIdCopyInputRef, () => {
      setTaskIdCopied(true);
      setTimeout(() => setTaskIdCopied(false), 2000);
    });
  };

  // Handle modal close
  const handleClose = () => {
    setLearnersList([]);
    onLicenseAllocationChange({});
    setSubmissionError('');
    setSubmissionSuccess('');
    setSubmissionTaskId(null);
    setTaskIdCopied(false);
    setIsBulkUploadMode(false);
    setBulkErrorLearners([]);
    setUploadStatus({ message: '', variant: 'info' });
    setLicenseSearchTerm('');
    setEditingIndex(null);
    setCurrentLearner(buildEmptyLearner());
    onClose();
  };

  return (
    <ModalDialog
      title="Create And Enroll New Learners"
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      hasCloseButton
      isFullscreenOnMobile
    >
      <ModalDialog.Header>
        <ModalDialog.Title>Create And Enroll New Learners</ModalDialog.Title>
      </ModalDialog.Header>

      <ModalDialog.Body>
        {submissionError && (
          <Alert variant="danger" dismissible onClose={() => setSubmissionError('')}>
            {submissionError}
          </Alert>
        )}

        {submissionSuccess && (
          <Alert variant="success" dismissible onClose={() => { setSubmissionSuccess(''); setSubmissionTaskId(null); }}>
            {submissionSuccess}
            {submissionTaskId && (
              <div className="mt-2 d-flex align-items-center" style={{ gap: '0.5rem' }}>
                <small className="text-muted">Task ID:</small>
                {/* Visible read-only input — copy source AND display for the task ID */}
                <input
                  ref={taskIdCopyInputRef}
                  readOnly
                  value={submissionTaskId}
                  style={{
                    flex: 1,
                    fontSize: '0.8rem',
                    fontFamily: 'monospace',
                    color: '#495057',
                    background: 'rgba(0,0,0,0.06)',
                    border: '1px solid #dee2e6',
                    borderRadius: '0.2rem',
                    padding: '0.1rem 0.4rem',
                    cursor: 'text',
                    minWidth: 0,
                  }}
                  onFocus={(e) => e.target.select()}
                />
                <OverlayTrigger
                  trigger={['hover', 'focus']}
                  placement="top"
                  overlay={<Tooltip id="copy-task-id-tooltip">{taskIdCopied ? 'Copied!' : 'Copy task ID'}</Tooltip>}
                >
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0"
                    onClick={handleCopyTaskId}
                    style={{ lineHeight: 1, minWidth: 0 }}
                  >
                    <Icon
                      src={taskIdCopied ? Check : ContentCopy}
                      style={{ width: '1rem', height: '1rem', color: taskIdCopied ? '#198754' : '#6c757d' }}
                    />
                  </Button>
                </OverlayTrigger>
              </div>
            )}
          </Alert>
        )}

        {/* Bulk Upload Toggle Button */}
        <div className="mb-4 text-center">
          <Button
            variant={isBulkUploadMode ? 'outline-primary' : 'primary'}
            onClick={() => {
              // If switching away from individual entry while editing, cancel the edit first
              if (!isBulkUploadMode && editingIndex !== null) {
                handleCancelEdit();
              }
              setIsBulkUploadMode(!isBulkUploadMode);
            }}
          >
            {isBulkUploadMode ? 'Back to Individual Entry' : 'Go To Bulk Upload'}
          </Button>
        </div>

        {/* Learners List Summary */}
        {learnersList.length > 0 && (
          <Alert variant="info" className="mb-3">
            <strong>{learnersList.length}</strong> learner(s) ready to submit
            <div className="mt-2">
              {learnersList.map((learner, learnerIndex) => (
                <Badge
                  key={learner.email}
                  variant="secondary"
                  className="mr-2 mb-1"
                  style={{ cursor: 'default', display: 'inline-flex', alignItems: 'center' }}
                >
                  <OverlayTrigger
                    trigger={['hover', 'focus']}
                    placement="top"
                    overlay={<Tooltip id={`edit-learner-tooltip-${learnerIndex}`}>Click to edit this learner</Tooltip>}
                  >
                    <span
                      role="button"
                      tabIndex={0}
                      className="learner-pill-edit-trigger"
                      onClick={() => {
                        setIsBulkUploadMode(false);
                        handleEditLearner(learnerIndex);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          setIsBulkUploadMode(false);
                          handleEditLearner(learnerIndex);
                        }
                      }}
                    >
                      <Icon src={Edit} />
                      {learner.firstName} {learner.lastName} ({learner.email})
                    </span>
                  </OverlayTrigger>
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 ml-1"
                    onClick={() => handleRemoveLearner(learnerIndex)}
                    style={{ color: 'white', textDecoration: 'none' }}
                  >
                    ✕
                  </Button>
                </Badge>
              ))}
            </div>
          </Alert>
        )}

        {/* Bulk Upload Section */}
        {isBulkUploadMode && (
          <div className="border rounded p-4 mb-4" style={{ backgroundColor: '#f8f9fa' }}>
            <h5 className="mb-3">Bulk CSV Upload</h5>

            {uploadStatus.message && (
              <Alert
                variant={uploadStatus.variant}
                dismissible
                onClose={() => setUploadStatus({ message: '', variant: 'info' })}
                className="mb-3"
              >
                {uploadStatus.message}
              </Alert>
            )}

            <div className="mb-3">
              <h6>Step 1: Download CSV Template</h6>
              <p className="text-muted small mb-2">
                Download the template below, which includes columns for all required fields and available licenses.
                Fill in learner information and use TRUE/FALSE for license enrollment.
                Remove the example row before uploading.
              </p>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleDownloadTemplate}
              >
                Download CSV Template
              </Button>
            </div>

            <div>
              <h6>Step 2: Upload Completed CSV</h6>
              <p className="text-muted small mb-2">
                Upload your completed CSV file. Valid entries will be added automatically,
                and any entries with errors will appear below for correction.
              </p>
              <Form.Group>
                <Form.Control
                  type="file"
                  accept=".csv"
                  onChange={handleCSVUpload}
                />
                <Form.Text className="text-muted">
                  You can upload multiple CSV files - they will be combined and duplicates will be skipped.
                </Form.Text>
              </Form.Group>
            </div>
          </div>
        )}

        {/* Bulk Error Correction Section */}
        {bulkErrorLearners.length > 0 && (
          <div className="bulk-error-correction-section">
            <Alert variant="warning" className="mb-3">
              <strong>{bulkErrorLearners.length}</strong> learner(s) have validation errors.
              Please correct the errors below and click &quot;Fix &amp; Add&quot; for each learner.
            </Alert>

            {bulkErrorLearners.map((errorLearner, errorIndex) => {
              // Check if learner has any remaining errors
              const hasErrors = Object.keys(errorLearner.errors).length > 0
                && (Object.keys(errorLearner.errors).some(key => {
                  if (key === 'customFields') {
                    return errorLearner.errors.customFields && Object.keys(errorLearner.errors.customFields).length > 0;
                  }
                  return true;
                }));

              return (
                <div key={errorLearner.rowNumber} className="bulk-error-item">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0">Row {errorLearner.rowNumber}</h6>
                    <div>
                      <Button
                        variant="success"
                        size="sm"
                        onClick={() => handleFixBulkErrorLearner(errorIndex)}
                        className="mr-2"
                        disabled={hasErrors}
                      >
                        Fix & Add
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleRemoveBulkErrorLearner(errorIndex)}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-2">
                        <Form.Label className="small mb-1">First Name</Form.Label>
                        <Form.Control
                          type="text"
                          size="sm"
                          value={errorLearner.firstName}
                          onChange={(e) => handleBulkErrorLearnerChange(errorIndex, 'firstName', e.target.value)}
                          isInvalid={!!errorLearner.errors.firstName}
                        />
                        {errorLearner.errors.firstName && (
                          <Form.Control.Feedback type="invalid">
                            {errorLearner.errors.firstName}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-2">
                        <Form.Label className="small mb-1">Last Name</Form.Label>
                        <Form.Control
                          type="text"
                          size="sm"
                          value={errorLearner.lastName}
                          onChange={(e) => handleBulkErrorLearnerChange(errorIndex, 'lastName', e.target.value)}
                          isInvalid={!!errorLearner.errors.lastName}
                        />
                        {errorLearner.errors.lastName && (
                          <Form.Control.Feedback type="invalid">
                            {errorLearner.errors.lastName}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-2">
                    <Form.Label className="small mb-1">Email</Form.Label>
                    <Form.Control
                      type="email"
                      size="sm"
                      value={errorLearner.email}
                      onChange={(e) => handleBulkErrorLearnerChange(errorIndex, 'email', e.target.value)}
                      isInvalid={!!errorLearner.errors.email}
                    />
                    {errorLearner.errors.email && (
                      <Form.Control.Feedback type="invalid">
                        {errorLearner.errors.email}
                      </Form.Control.Feedback>
                    )}
                  </Form.Group>

                  {selectedCompany?.custom_csv_fields && selectedCompany.custom_csv_fields.length > 0 && (
                    <Row>
                      {selectedCompany.custom_csv_fields.map((field) => (
                        <Col md={6} key={field}>
                          <Form.Group className="mb-2">
                            <Form.Label className="small mb-1">{field}</Form.Label>
                            <Form.Control
                              type="text"
                              size="sm"
                              value={errorLearner.customFields[field] || ''}
                              onChange={(e) => handleBulkErrorCustomFieldChange(errorIndex, field, e.target.value)}
                              isInvalid={!!errorLearner.errors.customFields?.[field]}
                            />
                            {errorLearner.errors.customFields?.[field] && (
                              <Form.Control.Feedback type="invalid">
                                {errorLearner.errors.customFields[field]}
                              </Form.Control.Feedback>
                            )}
                          </Form.Group>
                        </Col>
                      ))}
                    </Row>
                  )}

                  {errorLearner.selectedLicenses.length > 0 && (
                    <div className="mt-2">
                      <small className="text-muted">
                        Licenses: {errorLearner.selectedLicenses.map(id => licenseOptions.find(l => l.id === id)?.name || id).join(', ')}
                      </small>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {!isBulkUploadMode && (
          <Form>
            <h5 className="mb-3">Learner Information</h5>

            {/* Standard Fields */}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    First Name <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={currentLearner.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    isInvalid={!!validationErrors.firstName}
                  />
                  {validationErrors.firstName && (
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.firstName}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Last Name <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={currentLearner.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    isInvalid={!!validationErrors.lastName}
                  />
                  {validationErrors.lastName && (
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.lastName}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>
                Email <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="email"
                value={currentLearner.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                isInvalid={!!validationErrors.email}
              />
              {validationErrors.email && (
                <Form.Control.Feedback type="invalid">
                  {validationErrors.email}
                </Form.Control.Feedback>
              )}
            </Form.Group>

            {/* Dynamic Custom Fields */}
            {selectedCompany?.custom_csv_fields && selectedCompany.custom_csv_fields.length > 0 && (
              <>
                <h5 className="mb-3 mt-4">Custom Fields</h5>
                <Row>
                  {selectedCompany.custom_csv_fields.map((field) => (
                    <Col md={6} key={field}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          {field} <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          value={currentLearner.customFields[field] || ''}
                          onChange={(e) => handleCustomFieldChange(field, e.target.value)}
                          isInvalid={!!validationErrors.customFields?.[field]}
                        />
                        {validationErrors.customFields?.[field] && (
                          <Form.Control.Feedback type="invalid">
                            {validationErrors.customFields[field]}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col>
                  ))}
                </Row>
              </>
            )}

            {/* License Selection */}
            {licenseOptions.length > 0 && (
              <>
                <h5 className="mb-3 mt-4">Course Licenses (Optional)</h5>
                <Form.Text className="mb-2 d-block">
                  Select courses to enroll this learner in. Licenses with 0 available are disabled.
                </Form.Text>
                <Form.Control
                  type="text"
                  placeholder="Search by name or course ID..."
                  value={licenseSearchTerm}
                  onChange={(e) => setLicenseSearchTerm(e.target.value)}
                  className="mb-2"
                />
                <div className="border rounded p-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {licenseOptions
                    .filter((license) => {
                      if (!licenseSearchTerm.trim()) { return true; }
                      const term = licenseSearchTerm.toLowerCase();
                      if (license.name.toLowerCase().includes(term)) { return true; }
                      // For programs, also search within each contained course ID and display name
                      if (license.courseIds?.some((id) => id.toLowerCase().includes(term))) { return true; }
                      return false;
                    })
                    .map((license) => {
                      const allocated = licenseAllocation[license.id] || 0;
                      let remaining = getCatalogRemainingAvailability(license.available, allocated);
                      if (license.type === 'legacy_bundle' && license.available != null) {
                        remaining = license.available - license.used - allocated;
                      }

                      return (
                        <Form.Check
                          key={license.id}
                          type="checkbox"
                          id={`license-${license.id}`}
                          label={(
                            <span>
                              {license.name}
                              {' '}
                              <Badge variant={license.disabled ? 'danger' : 'success'}>
                                {typeof remaining === 'number' ? `${remaining} available` : 'Unlimited'}
                              </Badge>
                            </span>
                          )}
                          checked={currentLearner.selectedLicenses.includes(license.id)}
                          onChange={(e) => handleLicenseSelectionChange(license.id, e.target.checked)}
                          disabled={license.disabled}
                          className="mb-2"
                        />
                      );
                    })}
                  {licenseOptions.length > 0 && licenseSearchTerm.trim() && licenseOptions.filter((license) => {
                    const term = licenseSearchTerm.toLowerCase();
                    return license.name.toLowerCase().includes(term)
                      || license.courseIds?.some((id) => id.toLowerCase().includes(term));
                  }).length === 0 && (
                    <p className="text-muted mb-0">No licenses match your search.</p>
                  )}
                </div>
              </>
            )}
          </Form>
        )}
      </ModalDialog.Body>

      <ModalDialog.Footer>
        <ActionRow className="justify-content-between w-100">
          <div className="d-flex align-items-center">
            <ModalDialog.CloseButton variant="tertiary">
              Cancel
            </ModalDialog.CloseButton>
            {!isBulkUploadMode && !formValidation.isValid && (
              <small className="text-danger ml-3">{formValidation.message}</small>
            )}
          </div>
          <div className="d-flex">
            {!isBulkUploadMode && (
              <Button
                variant="outline-primary"
                onClick={handleAddLearner}
                disabled={!formValidation.isValid || isSubmitting}
                className="mr-2"
              >
                {editingIndex !== null ? 'Save Learner' : 'Add Learner'}
              </Button>
            )}
            {editingIndex !== null ? (
              <Button
                variant="outline-danger"
                onClick={handleCancelEdit}
                disabled={isSubmitting}
              >
                Cancel Edit
              </Button>
            ) : (
              <Button
                variant="primary"
                onClick={handleSubmitAll}
                disabled={learnersList.length === 0 || isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Spinner animation="border" size="sm" className="mr-2" />
                    Submitting...
                  </>
                ) : (
                  `Submit All (${learnersList.length})`
                )}
              </Button>
            )}
          </div>
        </ActionRow>
      </ModalDialog.Footer>
    </ModalDialog>
  );
};

export default CreateLearnersModal;
